import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCart, type CartKind, type UseCart } from '../viewmodels/useCart'

/**
 * CartContext — single source of truth for the visitor's cart across the SPA.
 *
 * Mounts inside AuthProvider (so the session cookie is already minted via
 * /session/init by the time the cart endpoints get called). Fetches /cart on
 * boot; every mutation keeps the in-memory cart fresh because the API returns
 * the updated cart each time.
 *
 * Also owns the cart-overlay open state: opening the cart is a global
 * concern (it's reachable from any storefront page's nav button), so the
 * `open()` / `close()` controls + the rendered overlay live here at the
 * provider level. The CartOverlay slides in right-to-left over whatever
 * page is underneath (same UX as StoryOverlay).
 */
export interface UseCartContext extends UseCart {
  readonly isOpen: boolean
  readonly open: () => void
  readonly close: () => void
}

const CartContext = createContext<UseCartContext | undefined>(undefined)

/** Surface discriminator derived from the URL. /build/* uses the build
 *  cart; every other route uses the shop cart. Migration 0102, Kiran
 *  2026-06-06. */
function kindFromPath(pathname: string): CartKind {
  return pathname === '/build' || pathname.startsWith('/build/') ? 'build' : 'shop'
}

export function CartProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  // useCart owns the wire + refetches itself whenever `kind` changes,
  // so simply passing the route-derived kind keeps the cart in sync as
  // the visitor moves between /shop and /build.
  const activeKind = useMemo(() => kindFromPath(location.pathname), [location.pathname])
  const cart = useCart(activeKind)
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])

  // Cross-route signal: any page (today, e.g., a post-checkout return
  // landing) can hand the customer back to the storefront with
  // `?openCart=1` to re-open the cart drawer. Strip the param so a
  // later refresh doesn't loop the open.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('openCart') !== '1') return
    setIsOpen(true)
    params.delete('openCart')
    const cleaned = params.toString()
    navigate(`${location.pathname}${cleaned === '' ? '' : `?${cleaned}`}${location.hash}`, { replace: true })
  }, [location.pathname, location.search, location.hash, navigate])

  const value: UseCartContext = { ...cart, isOpen, open, close }

  // The CartOverlay itself is mounted by <Overlays /> in AppRouter,
  // because the overlay calls useProfileContext() and ProfileProvider
  // sits inside CartProvider in the tree. A sibling-of-children mount
  // here can't see ProfileContext — moving the mount lower fixes that.
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}

export function useCartContext(): UseCartContext {
  const ctx = useContext(CartContext)
  if (ctx === undefined) {
    throw new Error('useCartContext must be used inside <CartProvider>')
  }
  return ctx
}
